import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info, MapPinPlus } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { CADModelSelect } from '../../../cad/CADModelSelect';
import { translateWithFallback } from '../../../../utils/translation';

interface CadLinkPromptProps {
  anchoredCadModelId?: string;
  unlinkedFailureModesCount: number;
  selectedCadModelId?: string;
  onSelectCadModel: (cadModelId: string | undefined) => void;
  onOpenPartLabelEditor: () => void;
  partLabelsJson: string;
  onPartLabelsJsonChange: (value: string) => void;
  partLabelsError: string | null;
  onLinkCad: () => void;
  isLinkingCad: boolean;
}

const CadLinkPrompt: React.FC<CadLinkPromptProps> = ({
  anchoredCadModelId,
  unlinkedFailureModesCount,
  selectedCadModelId,
  onSelectCadModel,
  onOpenPartLabelEditor,
  partLabelsJson,
  onPartLabelsJsonChange,
  partLabelsError,
  onLinkCad,
  isLinkingCad,
}) => {
  const { t } = useTranslation();

  return (
    <Alert variant="info" className="mb-4">
      <Info />
      <AlertDescription>
        <div className="flex items-start gap-4 flex-wrap">
          <p className="text-sm min-w-[240px]">
            {anchoredCadModelId
              ? translateWithFallback(t, 'drbfm.cadLink.unlinkedCount', `未リンクの失敗モードが${unlinkedFailureModesCount}件あります。`, {
                  count: unlinkedFailureModesCount,
                })
              : t('drbfm.cadLink.noLinks', 'このDRBFM結果にはまだCADリンクがありません。')}
          </p>
          <div className="min-w-[280px] flex-[1_1_280px] min-h-[86px]">
            <CADModelSelect
              value={selectedCadModelId || ''}
              onChange={onSelectCadModel}
              disabled={isLinkingCad}
              helperText={t('drbfm.cadLink.linkHelper', '選択したCADモデルに失敗モードをリンクします')}
            />
            <Button
              variant="outline"
              className="mt-2"
              onClick={onOpenPartLabelEditor}
              disabled={!selectedCadModelId || isLinkingCad}
            >
              <MapPinPlus />
              {t('drbfm.cadLink.labelEditor.open', '3Dで部位を登録')}
            </Button>
          </div>
          <div className="min-w-[280px] flex-[1_1_280px] min-h-[86px]">
            <Label htmlFor="drbfm-part-labels-json">
              {t('drbfm.cadLink.partLabelsJson', '部位ラベルJSON')}
            </Label>
            <Textarea
              id="drbfm-part-labels-json"
              className="mt-1 min-h-[38px] max-h-24"
              rows={1}
              value={partLabelsJson}
              onChange={(event) => onPartLabelsJsonChange(event.target.value)}
              disabled={isLinkingCad}
              aria-invalid={!!partLabelsError}
            />
            <p
              className={cn(
                'text-xs mt-1',
                partLabelsError ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {partLabelsError
                || t('drbfm.cadLink.partLabelsHelper', 'cover_part_labels.json全体、またはparts配列を貼り付けると優先ラベルとして使います')}
            </p>
          </div>
          {/* The row is items-start, so a bare button sits level with the JSON label
              rather than the field it submits. */}
          <Button
            className="self-center shrink-0"
            onClick={onLinkCad}
            disabled={!selectedCadModelId || isLinkingCad}
          >
            {isLinkingCad
              ? t('drbfm.cadLink.linking', 'リンク中...')
              : t('drbfm.cadLink.link', 'CADにリンク')}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default CadLinkPrompt;
